import cuid from "cuid";

import { publishToWorkspaceFeed } from "@/models/actions/clientActions";

/** A file the composer has already uploaded, waiting on Send to reach the channel. */
export type ComposerAttachment = {
  contentId: string;
  itemId: string;
  /** The file's name, for the chip the composer shows while the file waits. */
  name: string;
};

/** What a composer holds for one channel: the typed text and, at most, one uploaded file. */
export type ComposerDraft = {
  text: string;
  attachment: ComposerAttachment | null;
};

export const EMPTY_COMPOSER_DRAFT: ComposerDraft = Object.freeze({ text: "", attachment: null });

/** The longest text a composer sends; the driver list's footer holds the same line. */
export const COMPOSER_MAX_CHARS = 5000;

/** Whether the draft's text runs past the limit; the composer says so and holds the send. */
export function composerTextTooLong(draft: ComposerDraft): boolean {
  return draft.text.length > COMPOSER_MAX_CHARS;
}

/** Whether the draft has anything to send: text within the limit, or a file. */
export function composerDraftSendable(draft: ComposerDraft): boolean {
  if (composerTextTooLong(draft)) return false;
  return draft.text.trim().length > 0 || draft.attachment != null;
}

/** The publish a send goes through; the real one by default, a recorder in tests. */
export type ComposerPublish = (params: {
  workspaceId: string;
  feedId: string;
  itemId?: string;
  contentId: string;
  text?: string;
  isSilent?: boolean;
}) => Promise<unknown>;

/**
 * Sends a composer's draft to a channel: the text as one message, then the file as another, the way the driver list's
 * footer sends them. The text goes first so the driver hears the words before the file lands. The file item is silent,
 * as uploads are everywhere in the app (there is nothing to speak); the text takes the channel's own `isSilent`. A
 * draft with neither text nor file sends nothing.
 */
export async function sendComposerDraft({
  workspaceId,
  feedId,
  draft,
  isSilent,
  publish = publishToWorkspaceFeed,
}: {
  workspaceId: string;
  feedId: string;
  draft: ComposerDraft;
  /** The channel's silent setting, applied to the text. */
  isSilent: boolean | undefined;
  publish?: ComposerPublish;
}): Promise<void> {
  const text = draft.text.trim();
  if (text) {
    await publish({ workspaceId, feedId, contentId: cuid(), text, isSilent });
  }
  if (draft.attachment) {
    const { contentId, itemId } = draft.attachment;
    await publish({ workspaceId, feedId, contentId, itemId, isSilent: true });
  }
}
